// 관리자 주문 관리 화면 (운영 메인 화면).
// 설계 (작업지시 1~3,6,8):
//  - 좌(포장)/우(배달) 2분할로 두 유형을 한눈에 본다.
//  - 자동 폴링(refetchInterval)으로 새로고침 없이 신규 주문이 들어온다.
//  - 활성 주문(신규/수락/준비완료)은 항상 노출, 종결(완료/거절/취소)은 토글로만 노출.
//  - 카드를 펼치면 상세가 보이고, 상태변경 버튼은 접힌 상태에서도 항상 노출(개선항목 7).
//  - 신규(미수락) 주문 카드는 붉은 테두리로 강조(개선항목 6).
//  - 상태변경 버튼은 주문 정보 확인 모달을 거쳐 적용(개선항목 8).
//  - 신규(미수락) 주문이 있으면 수락할 때까지 사운드 반복 알람 (브라우저 자동재생 정책상 토글로 활성화).
//  - 로그인 후 처음 진입 시 신규주문 알림/소리 켜기 동의 모달(개선항목 5).

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ORDER_STATUS,
  ORDER_STATUS_ADMIN_LABEL_KO,
  ORDER_TYPE,
  PAYMENT_METHOD_TYPE,
  PAYMENT_METHOD_TYPE_LABEL_KO,
  PAYMENT_STATUS,
  PAYMENT_STATUS_LABEL_KO,
  PRINT_JOB_KIND,
  allowedNextOrderStatuses,
  computeSettledLineTotalKrw,
  formatWeightG,
  lineExpectedGramsG,
  lineExpectedPriceKrw,
  formatCartLineLabel,
  formatDesiredPickup,
  resolveUnitLabel,
  type AdminOrderDto,
  type AdminUpdateOrderStatusInput,
  type OrderItemDto,
  type OrderItemWeightInput,
  type OrderStatus,
  type PrintJobKind,
} from "@oo/shared";
import { PageHeader } from "@/shared/ui";
import { ApiError } from "@/api/client.ts";
import {
  listAdminOrders,
  markOrderPaid,
  reconcileOrderPayment,
  refundOrderPayment,
  reprintOrderReceipt,
  updateOrderStatus,
} from "@/features/orders/ordersApi.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";
import AdminPushToggle from "@/features/push/AdminPushToggle.tsx";
import { useNewOrders, type OrderSoundAlarm } from "@/features/orders/NewOrdersProvider.tsx";

// 활성(작업 대상) 상태 집합. 이 상태들은 항상 목록에 남는다.
const ACTIVE_STATUSES: OrderStatus[] = [
  ORDER_STATUS.RECEIVED,
  ORDER_STATUS.ACCEPTED,
  ORDER_STATUS.PREPARING,
  ORDER_STATUS.READY,
];

function isActive(status: OrderStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

// 주문 관리 화면 (포장/배달 2분할, 자동폴링, 상태변경).
// 신규 주문 소리 알람/동의 모달은 전역 NewOrdersProvider 로 이관되어 어느 화면에서든 동작한다.
export default function OrdersPage() {
  const qc = useQueryClient();
  const [showTerminal, setShowTerminal] = useState(false);

  // 전체 주문을 한 번에 받아 클라이언트에서 유형/활성여부로 분류한다 (단일 폴링으로 양 컬럼 동기화).
  const ordersQuery = useQuery({
    queryKey: ["admin-orders-all"],
    queryFn: () => listAdminOrders({}),
    // 새로고침 없이 신규 주문이 보이도록 짧게 폴링 + 포커스 시 갱신 + 백그라운드 탭도 갱신.
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const all = useMemo(() => ordersQuery.data ?? [], [ordersQuery.data]);

  // 유형/활성 분류.
  const split = useMemo(() => {
    const pickup = all.filter((o) => o.type === ORDER_TYPE.PICKUP);
    const delivery = all.filter((o) => o.type === ORDER_TYPE.DELIVERY);
    const pick = (list: AdminOrderDto[]) => ({
      active: list.filter((o) => isActive(o.status)),
      terminal: list.filter((o) => !isActive(o.status)),
    });
    return { pickup: pick(pickup), delivery: pick(delivery) };
  }, [all]);

  // 신규(미수락) 주문 수 - 화면 표시용.
  const receivedCount = useMemo(
    () => all.filter((o) => o.status === ORDER_STATUS.RECEIVED).length,
    [all],
  );

  // 소리 알람 컨트롤러 (전역 프로바이더가 소유 - 알람 토글 버튼에서 공유).
  const { alarm } = useNewOrders();

  const onChanged = () => {
    void qc.invalidateQueries({ queryKey: ["admin-orders-all"] });
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="주문 관리"
        description="포장/배달을 한눈에 보고 상태를 변경합니다. 8초마다 자동 갱신됩니다."
        actions={
          <>
            <AlarmButton alarm={alarm} />
            <AdminPushToggle />
          </>
        }
      />

      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-600">
          {ordersQuery.isFetching ? "갱신 중..." : "8초마다 자동 갱신"} · 신규{" "}
          <span className="font-semibold text-brand-700">{receivedCount}</span>건
        </p>
        <label className="flex items-center gap-1.5 text-xs text-ink-600">
          <input
            type="checkbox"
            checked={showTerminal}
            onChange={(e) => setShowTerminal(e.target.checked)}
          />
          완료/거절/취소 포함
        </label>
      </div>

      {/* 2분할: 포장 / 배달 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <OrderColumn
          title="포장"
          active={split.pickup.active}
          terminal={split.pickup.terminal}
          showTerminal={showTerminal}
          loading={ordersQuery.isLoading}
          onChanged={onChanged}
        />
        <OrderColumn
          title="배달"
          active={split.delivery.active}
          terminal={split.delivery.terminal}
          showTerminal={showTerminal}
          loading={ordersQuery.isLoading}
          onChanged={onChanged}
        />
      </div>
    </div>
  );
}

// 한 유형(포장/배달) 컬럼.
function OrderColumn({
  title,
  active,
  terminal,
  showTerminal,
  loading,
  onChanged,
}: {
  title: string;
  active: AdminOrderDto[];
  terminal: AdminOrderDto[];
  showTerminal: boolean;
  loading: boolean;
  onChanged: () => void;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-2 rounded-lg border border-ink-200 bg-ink-50 p-3">
      <div className="flex items-baseline justify-between px-1">
        <h2 className="text-base font-bold">{title}</h2>
        <span className="text-xs text-ink-600">진행 {active.length}건</span>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-ink-200/60" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 bg-white p-6 text-center text-xs text-ink-600">
          진행 중인 {title} 주문이 없습니다.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {active.map((o) => (
            <OrderCard key={o.id} order={o} onChanged={onChanged} />
          ))}
        </ul>
      )}

      {/* 종결 주문 (토글 시) */}
      {showTerminal && terminal.length > 0 ? (
        <>
          <p className="px-1 pt-2 text-xs font-semibold text-ink-600">완료/거절/취소</p>
          <ul className="flex flex-col gap-2">
            {terminal.map((o) => (
              <OrderCard key={o.id} order={o} onChanged={onChanged} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

// 주문 카드 - 요약 줄(클릭 시 상세 펼침) + 항상 보이는 상태변경 버튼 행.
function OrderCard({ order, onChanged }: { order: AdminOrderDto; onChanged: () => void }) {
  // 단위 라벨 표시용(DB 관리 단위).
  const units = useUnits();
  // 실중량 정산(PR-B): 무게가변 항목이 있고 아직 실중량 미확정이면 금액은 "예상".
  const hasAdjustable = order.items.some((it) => it.weightAdjustable);
  const isEstimate = hasAdjustable && !order.weighedAt;
  const [expanded, setExpanded] = useState(order.status === ORDER_STATUS.RECEIVED);
  // 확인 모달 대상 상태(null 이면 닫힘).
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const statusMut = useMutation({
    mutationFn: (input: AdminUpdateOrderStatusInput) => updateOrderStatus(order.id, input),
    onSuccess: () => {
      setPendingStatus(null);
      setErrorMsg(null);
      onChanged();
    },
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.message : "상태를 변경하지 못했습니다.");
    },
  });

  // 영수증 재출력(과제 2). 직전 출력 스냅샷을 인쇄 큐에 다시 적재한다(매장 에이전트가 폴링해 출력).
  const [reprintMsg, setReprintMsg] = useState<string | null>(null);
  const reprintMut = useMutation({
    mutationFn: (kind: PrintJobKind) => reprintOrderReceipt(order.id, kind),
    onSuccess: () => setReprintMsg("재출력을 요청했습니다. 잠시 후 프린터에서 출력됩니다."),
    onError: (err) =>
      setReprintMsg(
        err instanceof ApiError ? err.message : "재출력에 실패했습니다. 잠시 후 다시 시도하세요.",
      ),
  });

  // 현장/SMS 주문 수동 결제완료(인앱은 자동청구라 대상 아님). 성공 시 목록 갱신으로 결제 배지가 바뀐다.
  const [payMsg, setPayMsg] = useState<string | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const markPaidMut = useMutation({
    mutationFn: () => markOrderPaid(order.id),
    onSuccess: () => {
      setPayMsg("결제완료로 표시했습니다.");
      onChanged();
    },
    onError: (err) =>
      setPayMsg(err instanceof ApiError ? err.message : "결제완료 표시에 실패했습니다."),
  });
  const reconcileMut = useMutation({
    mutationFn: () => reconcileOrderPayment(order.id),
    onSuccess: () => {
      setPayMsg("포트원 결제 원장과 동기화했습니다.");
      onChanged();
    },
    onError: (err) =>
      setPayMsg(err instanceof ApiError ? err.message : "결제 상태 확인에 실패했습니다."),
  });
  const refundMut = useMutation({
    mutationFn: () => refundOrderPayment(order.id, refundReason.trim()),
    onSuccess: () => {
      setPayMsg("전액 환불이 완료되었습니다.");
      setRefundOpen(false);
      setRefundReason("");
      onChanged();
    },
    onError: (err) =>
      setPayMsg(err instanceof ApiError ? err.message : "환불 처리에 실패했습니다."),
  });
  // 주방 주문표는 수락 이후, 최종 영수증은 준비완료 이후에 출력된 적이 있으므로 그때부터 재출력 가능.
  const canReprintAccept = (
    [
      ORDER_STATUS.ACCEPTED,
      ORDER_STATUS.PREPARING,
      ORDER_STATUS.READY,
      ORDER_STATUS.COMPLETED,
    ] as OrderStatus[]
  ).includes(order.status);
  const canReprintReady = ([ORDER_STATUS.READY, ORDER_STATUS.COMPLETED] as OrderStatus[]).includes(
    order.status,
  );

  // 관리자가 누를 수 있는 정방향 상태 (취소는 별도 위험 버튼으로 분리).
  const nextStatuses = allowedNextOrderStatuses(order.status).filter(
    (s) => s !== ORDER_STATUS.CANCELED,
  );
  // 진행 도중 취소 가능 여부(개선): 신규는 '거절'이 그 역할을 하므로, 수락 이후 단계에서만 '주문 취소'를 노출한다.
  const canAdminCancel =
    order.status !== ORDER_STATUS.RECEIVED &&
    allowedNextOrderStatuses(order.status).includes(ORDER_STATUS.CANCELED);

  const isNew = order.status === ORDER_STATUS.RECEIVED;
  const firstName = order.items[0]?.name ?? "주문 상품";
  const moreCount = order.itemCount - 1;
  const summary = moreCount > 0 ? `${firstName} 외 ${moreCount}건` : firstName;

  return (
    // 신규(미수락) 주문은 붉은 테두리로 강조(개선항목 6).
    <li
      className={`rounded-lg bg-white ${
        isNew ? "border-2 border-danger-500" : "border border-ink-200"
      }`}
    >
      {/* 요약 줄 (클릭 시 펼침) */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 p-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-ink-600 tabular-nums">{order.orderNo}</span>
            <StatusChip status={order.status} />
            <PaymentChip order={order} />
          </div>
          <p className="mt-1 truncate text-sm font-medium">{summary}</p>
          <p className="mt-0.5 text-xs text-ink-600">
            {order.customer.name ?? "회원"} ·{" "}
            {formatDesiredPickup(order.desiredDate, order.desiredTime)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <span className="text-sm font-bold tabular-nums">
            {isEstimate ? (
              <span className="mr-1 text-[10px] font-normal text-ink-500">예상</span>
            ) : null}
            {order.totalKrw.toLocaleString()}원
          </span>
          <span className="text-[11px] text-ink-600">{expanded ? "접기 ▲" : "상세 ▼"}</span>
        </div>
      </button>

      {/* 상세 (펼침) */}
      {expanded ? (
        <div className="flex flex-col gap-3 border-t border-ink-100 p-3">
          {/* 주문자 / 수령 */}
          <div className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
            <Info label="주문자" value={order.customer.name ?? "회원"} />
            <Info
              label="연락처"
              value={order.customer.phone ? formatPhone(order.customer.phone) : "-"}
            />
            <Info label="수령" value={formatDesiredPickup(order.desiredDate, order.desiredTime)} />
            {order.delivery ? (
              <>
                <Info
                  label="받는 분"
                  value={`${order.delivery.recipientName} (${formatPhone(order.delivery.recipientPhone)})`}
                />
                <Info
                  label="배송지"
                  value={`${order.delivery.addressLine1}${order.delivery.addressLine2 ? ` ${order.delivery.addressLine2}` : ""}`}
                  wide
                />
              </>
            ) : null}
            {order.customerMemo ? <Info label="요청사항" value={order.customerMemo} wide /> : null}
            {order.status === ORDER_STATUS.REJECTED && order.rejectReason ? (
              <Info label="거절 사유" value={order.rejectReason} wide />
            ) : null}
          </div>

          {/* 상품 */}
          <ul className="flex flex-col gap-1 rounded-md bg-ink-50 p-2 text-xs">
            {order.items.map((it) => (
              <li key={it.id} className="flex flex-col gap-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate">
                    {it.name}{" "}
                    <span className="text-ink-600">
                      ({formatCartLineLabel(units, it.unit, it.quantity)})
                    </span>
                    {it.actualWeightG ? (
                      <span className="ml-1 text-[10px] text-brand-700">
                        실 {it.actualWeightG}g
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {it.lineTotalKrw.toLocaleString()}원
                  </span>
                </div>
                {/* 추가 옵션(금액은 위 줄 합계에 이미 포함). 주방이 손질할 추가 중량을 함께 보여준다. */}
                {it.options.map((o, oi) => (
                  <div key={oi} className="pl-3 text-[11px] text-ink-600">
                    + {o.label} x{o.quantity}
                    {o.addGrams > 0 ? ` (${formatWeightG(o.addGrams * o.quantity)})` : ""}
                  </div>
                ))}
              </li>
            ))}
            {order.appliedCoupons.length > 0 ? (
              <li className="flex items-baseline justify-between text-brand-700">
                <span>쿠폰 {order.appliedCoupons.map((c) => c.name).join(", ")}</span>
                <span className="tabular-nums">
                  {order.discountKrw > 0
                    ? `-${order.discountKrw.toLocaleString()}원`
                    : "배달비 무료"}
                </span>
              </li>
            ) : null}
            {order.complimentDiscountKrw > 0 ? (
              <li className="flex items-baseline justify-between text-brand-700">
                <span>
                  칭찬 감사 할인{order.complimentText ? ` (${order.complimentText})` : ""}
                </span>
                <span className="tabular-nums">
                  -{order.complimentDiscountKrw.toLocaleString()}원
                </span>
              </li>
            ) : null}
            {order.pointsUsedKrw > 0 ? (
              <li className="flex items-baseline justify-between text-brand-700">
                <span>적립금 사용</span>
                <span className="tabular-nums">-{order.pointsUsedKrw.toLocaleString()}원</span>
              </li>
            ) : null}
            <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 font-semibold">
              <span>
                {isEstimate ? "예상 합계" : "합계"} (
                {PAYMENT_METHOD_TYPE_LABEL_KO[order.paymentMethodType]})
              </span>
              <span className="tabular-nums">{order.totalKrw.toLocaleString()}원</span>
            </li>
          </ul>
          {isEstimate ? (
            <p className="text-[11px] text-ink-500">
              무게가변 상품은 준비완료 때 실제 중량을 입력하면 최종 금액이 확정됩니다.
            </p>
          ) : null}

          {/* 결제(인앱 PG): 결제수단/상태 + 현장·SMS 수동 결제완료 + 실패 안내 */}
          <div className="flex flex-col gap-1.5 border-t border-ink-100 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-ink-600">결제</span>
              <PaymentChip order={order} />
            </div>
            {order.paymentStatus === PAYMENT_STATUS.FAILED ? (
              <p className="text-[11px] text-danger-700">
                카드 결제에 실패했습니다. 준비완료를 다시 시도하거나, 손님에게 현장/문자 결제를
                안내해 주세요.
              </p>
            ) : null}
            {order.paymentStatus === PAYMENT_STATUS.CHARGING ? (
              <p className="text-[11px] text-warning-700">
                결제 처리 중입니다. 잠시 후 자동으로 갱신됩니다.
              </p>
            ) : null}
            {/* 현장/SMS 미결제: 수동 결제완료 버튼.
                인앱(빌링 자동청구)·일반 카드결제(결제창 즉시결제)는 PG 결제라 수동 결제완료 대상이 아니다. */}
            {order.paymentMethodType !== PAYMENT_METHOD_TYPE.INAPP &&
            order.paymentMethodType !== PAYMENT_METHOD_TYPE.INAPP_DIRECT &&
            order.paymentStatus === PAYMENT_STATUS.UNPAID ? (
              <button
                type="button"
                onClick={() => {
                  setPayMsg(null);
                  markPaidMut.mutate();
                }}
                disabled={markPaidMut.isPending}
                className="h-8 w-fit rounded-md border border-success-600/50 px-3 text-xs font-semibold text-success-700 hover:bg-success-50 disabled:opacity-50"
              >
                결제완료 표시
              </button>
            ) : null}
            {order.paymentStatus === PAYMENT_STATUS.REFUND_FAILED ? (
              <p className="text-[11px] text-danger-700">
                환불 완료를 확인하지 못했습니다. 아래 버튼으로 포트원 상태를 다시 확인해 주세요.
              </p>
            ) : null}
            {order.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT &&
            (order.paymentStatus === PAYMENT_STATUS.UNPAID ||
              order.paymentStatus === PAYMENT_STATUS.FAILED) ? (
              <button
                type="button"
                onClick={() => {
                  setPayMsg(null);
                  reconcileMut.mutate();
                }}
                disabled={reconcileMut.isPending}
                className="h-8 w-fit rounded-md border border-brand-600/50 px-3 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50"
              >
                {reconcileMut.isPending ? "확인 중..." : "PG 결제 상태 확인"}
              </button>
            ) : null}
            {(order.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP ||
              order.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT) &&
            (order.paymentStatus === PAYMENT_STATUS.PAID ||
              order.paymentStatus === PAYMENT_STATUS.REFUND_FAILED) ? (
              <button
                type="button"
                onClick={() => {
                  setPayMsg(null);
                  setRefundOpen(true);
                }}
                className="h-8 w-fit rounded-md border border-danger-500 px-3 text-xs font-semibold text-danger-700 hover:bg-danger-50"
              >
                {order.paymentStatus === PAYMENT_STATUS.REFUND_FAILED ? "환불 재확인" : "전액 환불"}
              </button>
            ) : null}
            {refundOpen ? (
              <div className="flex flex-col gap-2 rounded-md border border-danger-500/30 bg-danger-50 p-3">
                <p className="text-xs font-semibold text-danger-700">
                  포트원 카드 결제를 전액 취소합니다. 완료 주문이면 매출·판매량·적립금도 함께
                  정정됩니다.
                </p>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  maxLength={200}
                  rows={2}
                  placeholder="환불 사유 (2자 이상)"
                  className="rounded-md border border-ink-300 bg-white p-2 text-xs"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRefundOpen(false)}
                    disabled={refundMut.isPending}
                    className="h-8 rounded-md border border-ink-300 bg-white px-3 text-xs font-semibold"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={() => refundMut.mutate()}
                    disabled={refundMut.isPending || refundReason.trim().length < 2}
                    className="h-8 rounded-md bg-danger-600 px-3 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {refundMut.isPending ? "환불 확인 중..." : "전액 환불 실행"}
                  </button>
                </div>
              </div>
            ) : null}
            {payMsg ? <p className="text-[11px] text-ink-600">{payMsg}</p> : null}
          </div>

          {/* 영수증 재출력 (과제 2). 용지 걸림/누락 시 같은 영수증을 프린터로 다시 보낸다. */}
          {canReprintAccept || canReprintReady ? (
            <div className="flex flex-col gap-1.5 border-t border-ink-100 pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold text-ink-600">영수증 재출력</span>
                {canReprintAccept ? (
                  <button
                    type="button"
                    onClick={() => {
                      setReprintMsg(null);
                      reprintMut.mutate(PRINT_JOB_KIND.ACCEPT);
                    }}
                    disabled={reprintMut.isPending}
                    className="h-8 rounded-md border border-ink-300 px-3 text-xs font-semibold text-ink-700 hover:bg-ink-100 disabled:opacity-50"
                  >
                    주방 주문표
                  </button>
                ) : null}
                {canReprintReady ? (
                  <button
                    type="button"
                    onClick={() => {
                      setReprintMsg(null);
                      reprintMut.mutate(PRINT_JOB_KIND.READY);
                    }}
                    disabled={reprintMut.isPending}
                    className="h-8 rounded-md border border-ink-300 px-3 text-xs font-semibold text-ink-700 hover:bg-ink-100 disabled:opacity-50"
                  >
                    최종 영수증
                  </button>
                ) : null}
              </div>
              {reprintMsg ? <p className="text-[11px] text-ink-600">{reprintMsg}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 상태 변경 버튼 행: 접힌 상태에서도 항상 노출(개선항목 7). 클릭 시 확인 모달(개선항목 8). */}
      {nextStatuses.length > 0 || canAdminCancel ? (
        <div className="flex flex-wrap gap-2 border-t border-ink-100 p-3">
          {nextStatuses.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setErrorMsg(null);
                setPendingStatus(s);
              }}
              disabled={statusMut.isPending}
              className={`h-9 rounded-md px-4 text-sm font-semibold disabled:opacity-50 ${
                s === ORDER_STATUS.REJECTED
                  ? "border border-ink-400 text-ink-700 hover:bg-ink-100"
                  : "bg-brand-600 text-white hover:bg-brand-700"
              }`}
            >
              {ORDER_STATUS_ADMIN_LABEL_KO[s]}
            </button>
          ))}
          {/* 진행 도중 주문 취소(개선 1). 위험 동작이라 빨간 테두리 + 확인 모달을 거친다. */}
          {canAdminCancel ? (
            <button
              type="button"
              onClick={() => {
                setErrorMsg(null);
                setPendingStatus(ORDER_STATUS.CANCELED);
              }}
              disabled={statusMut.isPending}
              className="ml-auto h-9 rounded-md border border-danger-500 px-4 text-sm font-semibold text-danger-700 hover:bg-danger-50 disabled:opacity-50"
            >
              주문 취소
            </button>
          ) : null}
        </div>
      ) : null}

      {errorMsg ? <p className="px-3 pb-2 text-xs text-danger-700">{errorMsg}</p> : null}

      {/* 상태변경 확인 모달: 주문자/연락처/수령/품목 확인 후 예/아니요 (거절은 사유 입력). */}
      {pendingStatus ? (
        <OrderStatusConfirmModal
          order={order}
          targetStatus={pendingStatus}
          pending={statusMut.isPending}
          onCancel={() => setPendingStatus(null)}
          onConfirm={(payload) =>
            statusMut.mutate(
              pendingStatus === ORDER_STATUS.REJECTED
                ? { status: ORDER_STATUS.REJECTED, rejectReason: payload.reason ?? "" }
                : pendingStatus === ORDER_STATUS.READY
                  ? { status: ORDER_STATUS.READY, weights: payload.weights }
                  : { status: pendingStatus as AdminUpdateOrderStatusInput["status"] },
            )
          }
        />
      ) : null}
    </li>
  );
}

// 상태변경 확인 모달.
// 주문자/연락처/수령시간/주문 품목을 보여주고 "[라벨]으로 변경하시겠습니까?" 를 예/아니요로 확인한다(개선항목 8).
// 거절일 때는 손님 안내용 사유 입력을 받는다(필수).
// 항목의 예상 중량(g) = 기준중량*수량 + 추가옵션 중량 합계. 실중량 입력칸의 기본값으로 쓴다.
//  - 관리자는 이 줄의 "실제 총 중량"(기본+옵션)을 한 번 입력한다(합산 정산).
function estimatedWeightG(it: OrderItemDto): number | null {
  const g = lineExpectedGramsG(it.baseGramsSnapshot, it.quantity, it.options);
  return g > 0 ? g : null;
}

function OrderStatusConfirmModal({
  order,
  targetStatus,
  pending,
  onConfirm,
  onCancel,
}: {
  order: AdminOrderDto;
  targetStatus: OrderStatus;
  pending: boolean;
  onConfirm: (payload: { reason?: string; weights?: OrderItemWeightInput[] }) => void;
  onCancel: () => void;
}) {
  const units = useUnits();
  const isReject = targetStatus === ORDER_STATUS.REJECTED;
  const isCancel = targetStatus === ORDER_STATUS.CANCELED;
  const isReady = targetStatus === ORDER_STATUS.READY;
  const [reason, setReason] = useState("");
  const label = ORDER_STATUS_ADMIN_LABEL_KO[targetStatus];

  // 준비완료 + 무게가변 항목이 있으면 실중량을 입력받아 최종 금액을 확정한다(실중량 정산, PR-B).
  const weightItems = isReady ? order.items.filter((it) => it.weightAdjustable) : [];
  const showWeights = weightItems.length > 0;
  // 항목별 실중량 입력값(g, 문자열). 기본값 = 예상 중량.
  const [weights, setWeights] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const it of weightItems) {
      const est = estimatedWeightG(it);
      init[it.id] = est != null ? String(est) : "";
    }
    return init;
  });

  // 미리보기: 입력 실중량 기준으로 각 항목 라인과 새 상품합계를 계산한다(서버 계산과 동일한 공유 함수).
  function weightedLine(it: OrderItemDto): number {
    // 예상 금액 = 단가*수량 + 추가옵션 합계. 실중량이 들어오면 예상 총중량 대비 비율로 환산한다.
    const expectedPrice = lineExpectedPriceKrw(it.unitPriceKrw, it.quantity, it.options);
    if (it.weightAdjustable) {
      const w = Number.parseInt(weights[it.id] ?? "", 10);
      if (Number.isFinite(w) && w > 0) {
        const expectedG = lineExpectedGramsG(it.baseGramsSnapshot, it.quantity, it.options);
        const computed = computeSettledLineTotalKrw(expectedPrice, expectedG, w);
        if (computed > 0) {
          return computed;
        }
      }
    }
    return expectedPrice;
  }
  const previewSubtotal = order.items.reduce((sum, it) => sum + weightedLine(it), 0);

  const canConfirm = !pending && (!isReject || reason.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="order-confirm-title"
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-y-auto rounded-lg bg-surface p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="order-confirm-title" className="text-lg font-bold text-ink-900">
          주문 상태 변경
        </h2>

        {/* 주문 정보 요약 */}
        <dl className="mt-3 flex flex-col gap-1 rounded-md border border-ink-200 bg-ink-50 p-3 text-sm">
          <ModalRow label="주문번호" value={order.orderNo} />
          <ModalRow label="주문자" value={order.customer.name ?? "회원"} />
          <ModalRow
            label="연락처"
            value={order.customer.phone ? formatPhone(order.customer.phone) : "-"}
          />
          <ModalRow
            label="수령시간"
            value={formatDesiredPickup(order.desiredDate, order.desiredTime)}
          />
          {order.delivery ? (
            <ModalRow
              label="받는 분"
              value={`${order.delivery.recipientName} (${formatPhone(order.delivery.recipientPhone)})`}
            />
          ) : null}
        </dl>

        {/* 주문 품목 */}
        <ul className="mt-2 flex flex-col gap-1 rounded-md bg-ink-50 p-3 text-sm">
          {order.items.map((it) => (
            <li key={it.id} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 flex-1 truncate">
                {it.name}{" "}
                <span className="text-ink-600">
                  ({formatCartLineLabel(units, it.unit, it.quantity)})
                </span>
              </span>
              <span className="shrink-0 tabular-nums">{it.lineTotalKrw.toLocaleString()}원</span>
            </li>
          ))}
          <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 font-semibold">
            <span>{showWeights ? "예상 합계" : "합계 (현장 결제)"}</span>
            <span className="tabular-nums">{order.totalKrw.toLocaleString()}원</span>
          </li>
        </ul>

        {/* 실중량 입력 (준비완료 + 무게가변 항목). 입력 즉시 라인/상품합계 미리보기가 갱신된다. */}
        {showWeights ? (
          <div className="mt-3 rounded-md border border-brand-600/30 bg-brand-50/40 p-3">
            <p className="text-sm font-semibold text-ink-900">실제 중량 입력</p>
            <p className="mt-0.5 text-[11px] text-ink-600">
              손질·포장 후 실제 중량(g)을 입력하면 최종 금액이 확정됩니다. 비우면 예상 중량으로
              둡니다.
            </p>
            <ul className="mt-2 flex flex-col gap-2">
              {weightItems.map((it) => {
                const est = estimatedWeightG(it);
                return (
                  <li key={it.id} className="flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">
                      {it.name}{" "}
                      <span className="text-ink-500">({resolveUnitLabel(units, it.unit)})</span>
                      {/* 추가 옵션이 있으면 기본+옵션을 합친 총 중량을 재야 함을 명시한다. */}
                      {it.options.length > 0 ? (
                        <span className="ml-1 text-[11px] text-brand-700">
                          + {it.options.map((o) => `${o.label} x${o.quantity}`).join(", ")} 포함
                        </span>
                      ) : null}
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={weights[it.id] ?? ""}
                      onChange={(e) => setWeights((w) => ({ ...w, [it.id]: e.target.value }))}
                      placeholder={est != null ? String(est) : "g"}
                      className="w-24 rounded border border-ink-300 px-2 py-1 text-right text-sm tabular-nums"
                      aria-label={`${it.name} 실제 중량(g)`}
                    />
                    <span className="w-6 text-xs text-ink-500">g</span>
                    <span className="w-20 shrink-0 text-right text-sm tabular-nums text-ink-900">
                      {weightedLine(it).toLocaleString()}원
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 flex items-baseline justify-between border-t border-brand-600/20 pt-2 text-sm font-semibold">
              <span>새 상품합계</span>
              <span className="tabular-nums">{previewSubtotal.toLocaleString()}원</span>
            </div>
            <p className="mt-1 text-[11px] text-ink-500">
              최종 결제금액은 쿠폰·적립금 반영 후 확정되며, 확정 후 손님에게 안내됩니다.
            </p>
          </div>
        ) : null}

        {/* 거절 사유 입력 (거절일 때만, 손님에게 안내됨) */}
        {isReject ? (
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            rows={2}
            placeholder="거절 사유 (손님에게 안내됩니다)"
            className="mt-3 rounded-md border border-ink-300 p-2 text-sm"
          />
        ) : null}

        {/* 진행 도중 취소는 영향(쿠폰/적립금 환불)을 한 번 더 안내한다(개선 1). */}
        {isCancel ? (
          <p className="mt-3 rounded-md border border-danger-500/30 bg-danger-50 p-2 text-xs text-danger-700">
            진행 중인 주문을 취소합니다. 적용된 쿠폰과 사용한 적립금은 자동으로 환불되며, 손님에게도
            취소 안내가 전달됩니다.
          </p>
        ) : null}

        {/* 인앱 카드 결제: 준비완료 = 자동 청구임을 관리자에게 명확히 안내한다. */}
        {isReady && order.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP ? (
          <p className="mt-3 rounded-md border border-brand-600/30 bg-brand-50 p-2 text-xs text-brand-800">
            준비완료로 변경하면 손님의 등록 카드로 최종 금액이 자동 청구됩니다. 청구가 실패하면
            준비완료로 바뀌지 않습니다.
          </p>
        ) : null}

        <p className="mt-4 text-base font-semibold text-ink-900">
          {isCancel
            ? "이 주문을 취소하시겠습니까?"
            : `해당 주문을 [${label}]으로 변경하시겠습니까?`}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="inline-flex min-h-touch items-center justify-center rounded border border-ink-300 bg-surface px-4 text-base font-semibold text-ink-900 hover:bg-ink-50 disabled:opacity-50"
          >
            아니요
          </button>
          <button
            type="button"
            onClick={() => {
              // 유효한 실중량(양의 정수)만 weights 로 전송. 미입력 항목은 서버가 예상치를 유지한다.
              const builtWeights: OrderItemWeightInput[] = showWeights
                ? weightItems
                    .map((it) => ({
                      itemId: it.id,
                      actualWeightG: Number.parseInt(weights[it.id] ?? "", 10),
                    }))
                    .filter((w) => Number.isFinite(w.actualWeightG) && w.actualWeightG > 0)
                : [];
              onConfirm({
                reason: isReject ? reason.trim() : undefined,
                weights: showWeights ? builtWeights : undefined,
              });
            }}
            disabled={!canConfirm}
            className="inline-flex min-h-touch items-center justify-center rounded bg-brand-600 px-4 text-base font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {pending ? "처리 중..." : `예 (${label})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// 모달 정보 한 줄.
function ModalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-16 shrink-0 text-ink-600">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink-800">{value}</dd>
    </div>
  );
}

// 소리 알람 켜기/끄기 버튼 (컨트롤러는 상위에서 공유).
function AlarmButton({ alarm }: { alarm: OrderSoundAlarm }) {
  return (
    <button
      type="button"
      onClick={() => void alarm.toggle()}
      className={`h-8 rounded-md px-3 text-xs font-semibold ${
        alarm.enabled
          ? "bg-brand-600 text-white hover:bg-brand-700"
          : "border border-ink-300 text-ink-700 hover:bg-ink-100"
      }`}
      title="신규 주문이 있는 동안 소리로 알립니다"
    >
      {alarm.enabled ? "소리 알람 끄기" : "소리 알람 켜기"}
    </button>
  );
}

// 상태 칩 (관리자 라벨).
function StatusChip({ status }: { status: OrderStatus }) {
  const isCanceled = status === ORDER_STATUS.REJECTED || status === ORDER_STATUS.CANCELED;
  const isDone = status === ORDER_STATUS.COMPLETED;
  const isNew = status === ORDER_STATUS.RECEIVED;
  const className = isCanceled
    ? "bg-ink-200 text-ink-600"
    : isDone
      ? "bg-success-50 text-success-700"
      : isNew
        ? "bg-danger-600 text-white"
        : "bg-brand-600 text-white";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      {ORDER_STATUS_ADMIN_LABEL_KO[status]}
    </span>
  );
}

// 결제 상태 칩(결제수단 + 결제상태). 미결제는 옅게, 결제완료/실패/처리중은 강조한다.
function PaymentChip({ order }: { order: AdminOrderDto }) {
  const status = order.paymentStatus;
  const method = PAYMENT_METHOD_TYPE_LABEL_KO[order.paymentMethodType];
  const tone =
    status === PAYMENT_STATUS.PAID
      ? "bg-success-50 text-success-700"
      : status === PAYMENT_STATUS.FAILED || status === PAYMENT_STATUS.REFUND_FAILED
        ? "bg-danger-600 text-white"
        : status === PAYMENT_STATUS.CHARGING
          ? "bg-warning-50 text-warning-700"
          : status === PAYMENT_STATUS.REFUNDED
            ? "bg-danger-50 text-danger-700"
            : "bg-ink-100 text-ink-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {method} · {PAYMENT_STATUS_LABEL_KO[status]}
    </span>
  );
}

// 상세 정보 한 줄.
function Info({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`flex items-baseline gap-2 ${wide ? "sm:col-span-2" : ""}`}>
      <span className="w-14 shrink-0 text-ink-600">{label}</span>
      <span className="min-w-0 flex-1 text-ink-800">{value}</span>
    </div>
  );
}

// 휴대폰 표시 포맷.
function formatPhone(p: string): string {
  if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
  if (p.length === 10) return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;
  return p;
}
